import { useQuery, useMutation } from "@tanstack/react-query";
import { errorNotification } from "services/notificationService";
import useCancelableRequest, { CANCELED } from "./useCancelableRequest";

/**
 * Generic hook for paginated list queries with cancel support
 *
 * @param {string} key - Query key (e.g., "projects")
 * @param {Function} fetchFn - API fetch function (filters, cancelToken) => Promise
 * @param {Object} filters - Filters/search params
 */
export const useCustomQuery = ({
  keys,
  fetchFn,
  fullResponse = false,
  filters = {},
  options = {},
}) => {
  const cancelableFetch = useCancelableRequest(fetchFn);

  return useQuery({
    queryKey: keys,
    queryFn: async () => {
      const response = await cancelableFetch(filters);
      return fullResponse ? response : response?.data?.data ?? {};
    },
    onError: (error) => {
      if (error === CANCELED) return;

      errorNotification({
        message:
          error?.response?.data?.message || "حدث خطأ يرجى المحاولة لاحقاً",
      });
    },
    keepPreviousData: false,
    staleTime: 1000 * 60 * 10, // 10 minutes
    ...options,
  });
};

/**
 * Generic mutation hook with cancel support
 *
 * @param {Function} mutationFn - API mutation function (variables, cancelToken) => Promise
 * @param {Object} options - Additional options (onSuccess, onError)
 */
export const useCustomMutation = (mutationFn, options = {}) => {
  const cancelableMutation = useCancelableRequest(mutationFn);

  return useMutation({
    mutationFn: cancelableMutation,
    onError: (error) => {
      errorNotification({
        message:
          error?.response?.data?.message || "حدث خطأ يرجى المحاولة لاحقاً",
      });
    },
    ...options,
  });
};
